import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';

import AuthPage from './pages/AuthPage';
import ShipperDashboard from './components/ShipperDashboard';
import CarrierDashboard from './components/CarrierDashboard';
import BrokerDashboard from './components/BrokerDashboard';
import AdminDashboard from './components/AdminDashboard';
import ProtectedRoute from './ProtectedRoute';
import EmailVerification from './pages/EmailVerification';
import ConfirmPasswordChange from './pages/ConfirmPasswordChange';
import ResetPassword from './pages/ResetPassword';
import PaymentPage from './components/PaymentPage';
import AuthCallback from './components/AuthCallback';
import SetupProfilePage from './pages/SetupProfilePage';
import PendingApproval from './components/PendingApproval'; // ← import the page
import './styles.css';
import ForgotPassword from './pages/ForgotPassword';


// ─── DashboardRedirect ────────────────────────────────────────────────────────
const DashboardRedirect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleReturn = async () => {
      const token = localStorage.getItem('jwtToken');

      if (!token) {
        navigate('/');
        return;
      }

      try {
        sessionStorage.setItem('session_active', 'true');

        if (searchParams.get('payment_intent') || searchParams.get('setup_intent')) {
          const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
          await axios.post(`${API_BASE}/api/stripe/sync-subscription`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }

        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        const res = await axios.get(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const updatedUser = res.data;
        const role = updatedUser.role;
        localStorage.setItem('user', JSON.stringify(updatedUser));

        if (updatedUser.admin_approval_status === 'pending') {
          navigate('/pending-approval', { replace: true });
          return;
        }

        const validSubscriptionStatuses = ['active', 'trialing', 'on_trial', 'paid'];
        if (role === 'carrier' && !validSubscriptionStatuses.includes(updatedUser.subscription_status)) {
          navigate('/payment', { replace: true });
          return;
        }

        if (role === 'shipper')      navigate('/dashboard/shipper', { replace: true });
        else if (role === 'carrier') navigate('/dashboard/carrier', { replace: true });
        else if (role === 'broker')  navigate('/dashboard/broker',  { replace: true });
        else if (role === 'Admin')   navigate('/dashboard/admin',   { replace: true });
        else navigate('/');
      } catch (err) {
        console.error('Error refreshing profile after payment:', err);
        navigate('/');
      }
    };

    handleReturn();
  }, [navigate, searchParams]);

  return (
    <div className="loading-screen" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <h2>Finalizing your account setup...</h2>
      <p>Please wait while we verify your subscription.</p>
    </div>
  );
};


// ─── AppContent ───────────────────────────────────────────────────────────────
function AppContent() {
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isLoggedIn, setIsLoggedIn]         = useState(false);
  const [role, setRole]                     = useState(null);
  const [pendingUser, setPendingUser]       = useState(null); // ← NEW: holds user awaiting approval
  const [isLoggingIn, setIsLoggingIn]       = useState(false);
  const [sessionDialog, setSessionDialog]   = useState({ open: false, message: '', reason: '' });

  const navigate = useNavigate();
  const location = useLocation();

  // ── Initial auth check ────────────────────────────────────────────────────
  useEffect(() => {
    const initAuth = async () => {
      const token          = localStorage.getItem('jwtToken');
      const savedUser      = localStorage.getItem('user');
      const savedRole      = localStorage.getItem('role');
      const isSessionActive = sessionStorage.getItem('session_active') === 'true';

      if (token && savedUser && isSessionActive) {
        const parsedUser = JSON.parse(savedUser);
        setIsLoggedIn(true);
        setRole(savedRole);
        
        if (parsedUser.admin_approval_status === 'pending') {
          setPendingUser(parsedUser);
        }
        setIsAuthChecking(false);
      } else if (token && savedUser && !isSessionActive) {
        const channel = new BroadcastChannel('app_auth_sync');
        let sessionFound = false;

        const handleAuthMessage = (e) => {
          if (e.data === 'SESSION_ALIVE') {
            sessionFound = true;
            sessionStorage.setItem('session_active', 'true');
            setIsLoggedIn(true);
            setRole(savedRole);
            setIsAuthChecking(false);
          }
        };

        channel.addEventListener('message', handleAuthMessage);
        channel.postMessage('CHECK_SESSION');

        setTimeout(() => {
          channel.removeEventListener('message', handleAuthMessage);
          channel.close();
          if (!sessionFound) {
            localStorage.clear();
            setIsLoggedIn(false);
            setRole(null);
          }
          setIsAuthChecking(false);
        }, 150);
      } else {
        setIsLoggedIn(false);
        setRole(null);
        setIsAuthChecking(false);
      }
    };

    initAuth();
  }, []);

  // ── Broadcast: respond to other tabs ─────────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) return;
    const channel = new BroadcastChannel('app_auth_sync');
    const handleAuthRequest = (e) => {
      if (e.data === 'CHECK_SESSION') channel.postMessage('SESSION_ALIVE');
    };
    channel.addEventListener('message', handleAuthRequest);
    return () => {
      channel.removeEventListener('message', handleAuthRequest);
      channel.close();
    };
  }, [isLoggedIn]);

  // ── Auto-logout when navigating back to '/' while logged in ──────────────
  useEffect(() => {
    if (location.pathname === '/' && isLoggedIn && !isLoggingIn) {
      handleLogout();
    }
  }, [location, isLoggedIn, isLoggingIn]);

  useEffect(() => {
    if (location.pathname !== '/') setIsLoggingIn(false);
  }, [location]);

  // ── Carrier subscription enforcement ─────────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) return;

    const checkSession = async () => {
      try {
        const token       = localStorage.getItem('jwtToken');
        const currentRole = localStorage.getItem('role');
        if (!token) return;

        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) return;

        if (currentRole === 'carrier') {
          const userData = await res.json();
          const validStatuses = ['active', 'trialing', 'on_trial', 'paid'];
          if (!validStatuses.includes(userData.subscription_status) && window.location.pathname !== '/payment') {
            localStorage.setItem('user', JSON.stringify(userData));
            navigate('/payment');
          }
        }
      } catch {
        // silent
      }
    };

    checkSession();
    const intervalId = setInterval(checkSession, 30000);
    return () => clearInterval(intervalId);
  }, [isLoggedIn, navigate]);

  // ── Global fetch interceptor for 401 / session expired ───────────────────
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const response       = await originalFetch(...args);
      const clonedResponse = response.clone();
      if (response.status === 401) {
        try {
          const data = await clonedResponse.json();
          if (data.sessionExpired) {
            let message = data.message || 'Your session has expired. Please login again.';
            if (data.reason === 'session_invalidated') {
              message = 'You have been signed in from another device. Please login again to continue.';
            }
            localStorage.removeItem('jwtToken');
            localStorage.removeItem('user');
            localStorage.removeItem('role');
            sessionStorage.removeItem('session_active');
            setIsLoggedIn(false);
            setRole(null);
            setSessionDialog({ open: true, message, reason: data.reason || 'expired' });
          }
        } catch {
          console.debug('Non-JSON 401 response, ignoring session check');
        }
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, [navigate]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSessionDialogClose = () => {
    setSessionDialog({ open: false, message: '', reason: '' });
    navigate('/', { replace: true });
  };

  /**
   * Called by AuthPage after a successful login.
   *
   * If the user is pending approval → show PendingApproval screen.
   * Otherwise → set app state and navigate to dashboard.
   *
   * @param {string} signedInRole
   * @param {object} [userData]   full user object from the login response
   */
  const handleSignInSuccess = (signedInRole, userData) => {
    sessionStorage.setItem('session_active', 'true');
    setIsLoggingIn(true);
    setIsLoggedIn(true);
    setRole(signedInRole);

    // ── Pending approval case ──────────────────────────────────────────────
    if (userData?.admin_approval_status === 'pending') {
      setPendingUser(userData);
      navigate('/pending-approval', { replace: true });
      return;
    }

    // ── Normal case ───────────────────────────────────────────────────────
    if (signedInRole === 'shipper')      navigate('/dashboard/shipper');
    else if (signedInRole === 'carrier') navigate('/dashboard/carrier');
    else if (signedInRole === 'broker')  navigate('/dashboard/broker');
    else if (signedInRole === 'Admin')   navigate('/dashboard/admin');
  };

  /**
   * Called by PendingApproval once the admin approves the user and the
   * 2.5 s animation has played out.
   *
   * @param {object} approvedUser fresh user object from the last poll
   */
  const handlePendingApprovalComplete = (approvedUser) => {
    const approvedRole = approvedUser?.role ?? pendingUser?.role;
    localStorage.setItem('role', approvedRole);
    setIsLoggedIn(true);
    setRole(approvedRole);
    setPendingUser(null);

    if (approvedRole === 'shipper')      navigate('/dashboard/shipper', { replace: true });
    else if (approvedRole === 'carrier') navigate('/dashboard/carrier', { replace: true });
    else if (approvedRole === 'broker')  navigate('/dashboard/broker',  { replace: true });
    else if (approvedRole === 'Admin')   navigate('/dashboard/admin',   { replace: true });
    else navigate('/dashboard', { replace: true });
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (token) {
        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        await axios.post(`${API_BASE}/api/auth/signout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      setPendingUser(null);
      setIsLoggedIn(false);
      setRole(null);
      navigate('/', { replace: true });
    }
  };

  if (isAuthChecking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/"  element={<AuthPage onSignInSuccess={handleSignInSuccess} />} />
        <Route path="/auth/callback" element={<AuthCallback onSignInSuccess={handleSignInSuccess} />} />
        <Route path="/setup-profile" element={<SetupProfilePage />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />
        <Route path="/payment"   element={<PaymentPage />} />
        <Route path="/verify"    element={<EmailVerification />} />
        <Route path="/confirm-password-change" element={<ConfirmPasswordChange />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"          element={<ResetPassword />} />

        {/* ── Pending approval screen ───────────────────────────────────── */}
        <Route
          path="/pending-approval"
          element={
            <PendingApproval
              user={pendingUser}
              onComplete={handlePendingApprovalComplete}
            />
          }
        />

        {/* ── Protected dashboards ──────────────────────────────────────── */}
        <Route path="/dashboard/shipper/*" element={<ProtectedRoute><ShipperDashboard onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="/dashboard/carrier/*" element={<ProtectedRoute><CarrierDashboard onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="/dashboard/broker/*"  element={<ProtectedRoute><BrokerDashboard  onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="/dashboard/admin/*"   element={<ProtectedRoute><AdminDashboard   onLogout={handleLogout} /></ProtectedRoute>} />
      </Routes>

      {/* ── Session-expired dialog ────────────────────────────────────────── */}
      <Dialog
        open={sessionDialog.open}
        onClose={handleSessionDialogClose}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, textAlign: 'center' }}>
          {sessionDialog.reason === 'session_invalidated' ? 'Signed In Elsewhere' : 'Session Expired'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.95rem', textAlign: 'center' }}>
            {sessionDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleSessionDialogClose}
            variant="contained"
            color="primary"
            sx={{ backgroundColor: '#11224d' }}
            fullWidth
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}